import { EFinancingProgram, EPaymentFrequency, EProvince } from '@if/core/types'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { createContext, useContext } from 'react'

export interface BaseWorksheetFormConfig {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  merchantId: string

  minLoanAmount: number
  maxLoanAmount: number

  minLoanTerm: number
  maxLoanTerm: number
  largeLoanAmount: number

  effectiveRateThreshold: number

  supportedPaymentFrequencies: EPaymentFrequency[]
  supportsInsurance: boolean
  supportsHighEstimate: boolean
  useAgentLeeway: boolean
  validateMaxPayment: boolean
  filteredTermsByMaxPayment: boolean
  checkAvailableCredit: boolean
  checkActivationWithin90Days: boolean

  normsCreditDecision: NormCreditDecision | null
  maxAmountFinanced?: number | null
  maxTermDuration?: number | null
  canAcceptHighEstimate?: boolean

  hasCoApplicant: boolean
  stateIso: EProvince

  createdOn: Date

  expiresOn: Date | string
  editLocked?: boolean
  isSinglePayment?: boolean
}

const BaseWorksheetFormConfigContext = createContext<BaseWorksheetFormConfig | null>(null)

export const BaseWorksheetFormConfigProvider = BaseWorksheetFormConfigContext.Provider

export const useBaseWorksheetFormConfig = (): BaseWorksheetFormConfig => {
  const value = useContext(BaseWorksheetFormConfigContext)
  if (!value) {
    throw new Error('useBaseWorksheetFormConfig must be used inside <BaseWorksheetForm config={...}>')
  }
  return value
}

export interface WorksheetSimulationPricing {
  interestRate: number
  maxPmtAmount: number
}

const WorksheetSimulationPricingContext = createContext<WorksheetSimulationPricing | null>(null)

export const WorksheetSimulationPricingProvider = WorksheetSimulationPricingContext.Provider

export const useWorksheetSimulationPricing = (): WorksheetSimulationPricing | null =>
  useContext(WorksheetSimulationPricingContext)

// 'present' allows 0: a program may legitimately have no minimum loan amount.
const REQUIRED_FINANCING_CONFIG_KEYS = {
  effectiveRateThreshold: 'positive',
  minLoanAmount: 'present',
  maxLoanAmount: 'positive',
  minLoanTerm: 'positive',
  maxLoanTerm: 'positive',
} as const satisfies Partial<Record<keyof BaseWorksheetFormConfig, 'present' | 'positive'>>

export type RequiredFinancingConfigKey = keyof typeof REQUIRED_FINANCING_CONFIG_KEYS

export const findMissingFinancingConfigKeys = (
  config: Pick<BaseWorksheetFormConfig, RequiredFinancingConfigKey>,
): RequiredFinancingConfigKey[] =>
  (Object.keys(REQUIRED_FINANCING_CONFIG_KEYS) as RequiredFinancingConfigKey[]).filter((key) =>
    REQUIRED_FINANCING_CONFIG_KEYS[key] === 'present' ? config[key] == null : !config[key],
  )
